// physics.js - Movement, collisions, and game mechanics
import { sfx } from './audio';
import { spawnBurst } from './particles';

export const updatePlayerMovement = (game, canvasWidth) => {
  const p = game.player;
  
  // Handle keyboard input (canonical __left/__right flags, set by the input
  // layer or replayed by the validator — keeps movement deterministic).
  if (game.keys['__left']) {
    p.vx = -game.moveSpeed * game.speedModifier;
  } else if (game.keys['__right']) {
    p.vx = game.moveSpeed * game.speedModifier;
  } else if (game.options.tiltEnabled && game.tiltActive) {
    // Tilt steering: angle past a small deadzone maps proportionally to speed,
    // reaching full speed at ~24° from the calibrated neutral. (Replays never
    // hit this branch — tiltActive is only ever set by live device events.)
    const max = game.moveSpeed * game.speedModifier;
    const dead = 2, full = 16;
    const angle = game.tiltGamma - game.tiltBaseline;
    const mag = Math.abs(angle);
    if (mag <= dead) {
      p.vx = 0;
    } else {
      const n = Math.min((mag - dead) / (full - dead), 1);
      p.vx = Math.sign(angle) * n * max;
    }
  } else if (game.isTouching) {
    // Relative slide: move Specky by however far the finger has slid since the
    // last tick, not by where the finger is. Because it's relative, lifting and
    // re-touching elsewhere never jumps the character. Capped to the same top
    // speed as the keys so a fast flick can't fling Specky flat across the screen
    // — horizontal stays slow enough that jumps read as a proper arc.
    const max = game.moveSpeed * game.speedModifier;
    p.vx = Math.max(-max, Math.min(max, game.touchDragDX));
    game.touchDragDX = 0;
  } else {
    p.vx *= 0.8; // Friction
  }

  // Apply gravity or rocket boost
  if (game.rocketActive) {
    if (p.y <= game.rocketEndY) {
      game.rocketActive = false;
      p.vy = 0;
    } else {
      p.vy = -12;
    }
  } else {
  p.vy += game.gravity * game.speedModifier;
}

  // Update position
  p.x += p.vx;
  p.y += p.vy;

  // Wrap around screen horizontally
  if (p.x > canvasWidth) p.x = 0;
  if (p.x < -p.w) p.x = canvasWidth;
  
  // Update floating animation
  p.floatOffset = Math.sin(game.animationFrame * 0.05) * 3;

// Stretch vertically when moving fast upward (but don't override squash from landing)
if (p.vy < -5 && p.squash > 0.95) {
  p.squash = 1.3;
  p.stretch = 0.85;
}

// Extra stretch during rocket boost (overrides everything)
if (game.rocketActive) {
  p.squash = 1.6;
  p.stretch = 0.7;
} else {
  // Only smooth return to normal if not in special state
  p.squash += (1.0 - p.squash) * 0.08;
  p.stretch += (1.0 - p.stretch) * 0.08;
}
};

export const checkPlatformCollisions = (game) => {
  const p = game.player;
  
  if (p.vy > 0 && !game.rocketActive) {
    game.platforms.forEach(plat => {
      if (plat.broken) return;
      
      // Check if player is landing on platform
      if (
        p.x + p.w > plat.x &&
        p.x < plat.x + plat.w &&
        p.y + p.h >= plat.y &&
        p.y + p.h <= plat.y + plat.h + 10 &&
        p.vy > 0
      ) {
// Trigger squash animation BEFORE bouncing
const heightBefore = p.h * p.squash;
p.squash = 0.8;
p.stretch = 1.6;
const heightAfter = p.h * p.squash;
// Move player down by the difference to keep bottom aligned with platform
p.y += (heightBefore - heightAfter);

        if (plat.type === 'fragile') {
          plat.broken = true;
        } else if (plat.type === 'breakable') {
  const jumpModifier = Math.sqrt(game.speedModifier);
  p.vy = game.jumpPower * jumpModifier;
  plat.broken = true;
  sfx.jump();
  spawnBurst(game, p.x + p.w / 2, plat.y, '#cfe3ff', 6, 2.5); // bounce dust
        } else {
  // Check if platform has spring
// Adjust jump power to compensate for speed modifier so height stays same
const jumpModifier = Math.sqrt(game.speedModifier);
if (plat.hasSpring) {
  p.vy = game.springJumpPower * jumpModifier;
  plat.springCompression = 1.0;  // fully compress spring on contact
  sfx.spring();
  spawnBurst(game, p.x + p.w / 2, plat.y, '#00d4ff', 14, 4.5); // big spring pop
} else {
  p.vy = game.jumpPower * jumpModifier;
  sfx.jump();
  spawnBurst(game, p.x + p.w / 2, plat.y, '#cfe3ff', 6, 2.5); // bounce dust
}
}
      }
    });
  }
  
};

export const checkCoinCollisions = (game) => {
  const p = game.player;

  game.coins.forEach(coin => {
    if (coin.collected) return;

    const dx = (p.x + p.w/2) - coin.x;
    const dy = (p.y + p.h/2) - coin.y;
    const dist = Math.sqrt(dx*dx + dy*dy);

    if (dist < coin.r + p.w/2) {
  coin.collected = true;
  game.coinsCollectedCount += 1; // raw count for the X/Y display
  // Double coins if doublePoints boost is active (counts toward rocket fuel)
  const coinValue = game.activeBoost === 'doublePoints' ? 2 : 1;
  game.collectedCoins += coinValue;
  game.coinsForRocket += coinValue;
  sfx.coin();
  spawnBurst(game, coin.x, coin.y, '#f7b500', 8, 3);
}
  });
};

export const checkBoostCollisions = (game, activateBoostCallback) => {
  const p = game.player;
  
  game.boosts.forEach(boost => {
    if (boost.collected) return;
    
    if (
      p.x + p.w > boost.x &&
      p.x < boost.x + boost.w &&
      p.y + p.h > boost.y &&
      p.y < boost.y + boost.h
    ) {
      boost.collected = true;
      activateBoostCallback(boost.type);
    }
  });
};

export const checkEnemyCollisions = (game) => {
  if (game.rocketActive || game.shieldActive || !game.options.enemiesEnabled) {
    return false;
  }
  
  const p = game.player;
  
  for (let i = 0; i < game.enemies.length; i++) {
    const enemy = game.enemies[i];
    const dx = (p.x + p.w/2) - (enemy.x + enemy.w/2);
    const dy = (p.y + p.h/2) - (enemy.y + enemy.h/2);
    const dist = Math.sqrt(dx*dx + dy*dy);
    
    // Slightly forgiving circular hitbox: sprites are drawn with transparent
    // padding, so a tighter radius makes near-misses feel fair rather than cheap.
    if (dist < (p.w / 2 + enemy.w / 2 - 14)) {
      game.hitEnemy = true;
      return true;
    }
  }
  
  return false;
};

export const updateProjectiles = (game) => {
  game.projectiles = game.projectiles.filter(proj => {
    proj.x += proj.vx;
    proj.y += proj.vy;
    
    // Remove if off screen
    if (proj.y < -50 || proj.y > 1000 || proj.x < -50 || proj.x > 1000) {
      return false;
    }
    
    // Check collision with enemies
    for (let i = 0; i < game.enemies.length; i++) {
      const enemy = game.enemies[i];
      if (
        proj.x + proj.w > enemy.x &&
        proj.x < enemy.x + enemy.w &&
        proj.y + proj.h > enemy.y &&
        proj.y < enemy.y + enemy.h
      ) {
        enemy.health--;
        game.enemyHits = (game.enemyHits || 0) + 1; // for accuracy

        // Enemy pops when health reaches 0
        if (enemy.health <= 0) {
  spawnBurst(game, enemy.x + enemy.w / 2, enemy.y + enemy.h / 2, '#ff5a5a', 16, 5);
  game.enemies.splice(i, 1);
  game.enemiesKilled = (game.enemiesKilled || 0) + 1;
  sfx.kill();
}
        
        return false; // Remove projectile
      }
    }

    // Check collision with boosts
for (let i = 0; i < game.boosts.length; i++) {
  const boost = game.boosts[i];
  if (boost.collected) continue;
  
  if (
    proj.x + proj.w > boost.x &&
    proj.x < boost.x + boost.w &&
    proj.y + proj.h > boost.y &&
    proj.y < boost.y + boost.h
  ) {
    boost.collected = true;
    // Trigger the boost collection
    if (game.boostCollisionCallback) {
      game.boostCollisionCallback(boost.type);
    }
    return false; // Remove projectile
  }
}
// Check collision with coins
for (let i = 0; i < game.coins.length; i++) {
  const coin = game.coins[i];
  if (coin.collected) continue;
  
  if (
    proj.x + proj.w > coin.x - coin.r &&
    proj.x < coin.x + coin.r &&
    proj.y + proj.h > coin.y - coin.r &&
    proj.y < coin.y + coin.r
  ) {
    coin.collected = true;
game.coinsCollectedCount += 1;
const coinValue = game.activeBoost === 'doublePoints' ? 2 : 1;
game.collectedCoins += coinValue;
game.coinsForRocket += coinValue;
sfx.coin();
spawnBurst(game, coin.x, coin.y, '#f7b500', 8, 3);
    return false; // Remove projectile
  }
}
    
    return true;
  });
};

// Current climb height in metres, derived from the camera position.
const currentHeightOf = (game) => Math.floor(-game.cameraY / 10);

export const updateMovingPlatforms = (game, canvasWidth) => {
  // Moving platforms get gradually faster with altitude (up to +40% by ~800m).
  const speedScale = 1 + Math.min(currentHeightOf(game) / 800, 1) * 0.4;
  game.platforms.forEach(plat => {
    if (plat.type === 'moving') {
      plat.x += plat.direction * game.platformSpeed * speedScale;
      if (plat.x < 0 || plat.x > canvasWidth - plat.w) {
        plat.direction *= -1;
      }
    }
  });
};

export const updateMovingEnemies = (game, canvasWidth) => {
  // Enemies patrol faster the higher you climb (up to +80% by ~600m).
  const speedScale = 1 + Math.min(currentHeightOf(game) / 600, 1) * 0.8;
  game.enemies.forEach((enemy, index) => {
    // Floating animation for ALL enemies
    enemy.floatOffset = Math.sin((game.animationFrame + index * 30) * 0.04) * 5;

    if (enemy.type === 'horizontal') {
      // Horizontal movement
      enemy.x += enemy.direction * enemy.speed * speedScale;
      if (enemy.x < 0 || enemy.x > canvasWidth - enemy.w) {
        enemy.direction *= -1;
      }
      // Update facing based on direction
      enemy.facingRight = (enemy.direction === 1);
    } else if (enemy.type === 'stationary') {
      // Stationary bears float up and down in small area
      enemy.y = enemy.baseY + Math.sin((game.animationFrame + index * 20) * 0.03) * 15;
    }
  });
};

export const updateCoinRotation = (game) => {
  game.coins.forEach(coin => {
    coin.angle += 0.03;
  });
};

export const updateMagnetEffect = (game) => {
  if (!game.magnetActive) return;
  
  const p = game.player;
  const attractionSpeed = 8;
  const attractionRange = 150;
  
  game.coins.forEach(coin => {
    if (coin.collected) return;
    
    const dx = (p.x + p.w/2) - coin.x;
    const dy = (p.y + p.h/2) - coin.y;
    const dist = Math.sqrt(dx*dx + dy*dy);
    
    if (dist < attractionRange && dist > 0) {
      coin.x += (dx / dist) * attractionSpeed;
      coin.y += (dy / dist) * attractionSpeed;
    }
  });
};

export const shootProjectile = (game) => {
  if (game.bulletsRemaining <= 0) return;

  game.bulletsRemaining--;
  game.shotsFired = (game.shotsFired || 0) + 1;
  sfx.shoot();
  
  const p = game.player;
  let targetX = p.x + p.w/2;
  let targetY = -100;
  
  // Find closest enemy for autoaim
  if (game.enemies.length > 0) {
    let closestEnemy = null;
    let minDist = Infinity;
    
    game.enemies.forEach(enemy => {
      const dx = (enemy.x + enemy.w/2) - (p.x + p.w/2);
      const dy = (enemy.y + enemy.h/2) - (p.y + p.h/2);
      const dist = Math.sqrt(dx*dx + dy*dy);
      
      if (dist < minDist) {
        minDist = dist;
        closestEnemy = enemy;
      }
    });
    
    if (closestEnemy) {
      targetX = closestEnemy.x + closestEnemy.w/2;
      targetY = closestEnemy.y + closestEnemy.h/2;
    }
  }
  
  // Calculate direction
  const dx = targetX - (p.x + p.w/2);
  const dy = targetY - (p.y + p.h/2);
  const dist = Math.sqrt(dx*dx + dy*dy);
  
  game.projectiles.push({
    x: p.x + p.w/2 - 2,
    y: p.y,
    w: 4,
    h: 10,
    vx: (dx / dist) * 8,
    vy: (dy / dist) * 8
  });
};

export const updateSpringAnimations = (game) => {
  game.platforms.forEach(plat => {
    if (plat.springCompression > 0) {
      plat.springCompression -= 0.15;  // Spring recovers quickly
      if (plat.springCompression < 0) plat.springCompression = 0;
    }
  });
};